import { usePerson } from "../../app/hooks/user/usePerson";
import { useAdmin, ESTABLISHMENTS_STATUS } from "./useAdmin";
import { USER_STATUS } from "../common/model/enum";

/**
 * Source unique de vérité pour les prérequis d'accès à /admin, consommée à
 * la fois par la navigation de gauche (Menu.js) et par le routage
 * (AppDashboard.js) afin d'éviter toute duplication des règles d'accès.
 *
 * Règle : profil activé ET au moins un établissement ACTIVATED (liste centrale
 * d'AdminProvider, chargée dès l'arrivée sur /admin). Le statut de l'organisme
 * n'entre plus en jeu.
 */
export const useAdminAccessControl = () => {
  const { personInfo } = usePerson();
  const {
    organization,
    establishments,
    hasValidEstablishment,
    establishmentsStatus,
    refetchEstablishments,
  } = useAdmin();

  const isPersonActivated = personInfo?.status === USER_STATUS.ACTIVATED;
  const hasOrganization = Boolean(organization);
  const hasEstablishments = establishments.length > 0;

  // Déverrouillage complet de /admin (contenu + menu de gauche) : toute autre
  // combinaison garde l'écran d'activation affiché et le menu grisé.
  const isAdminUnlocked = isPersonActivated && hasValidEstablishment;
  const isAdminAccessBlocked = !isAdminUnlocked;

  // Chargement / échec de la liste : ils ne comptent qu'une fois le profil
  // activé (avant, c'est l'étape 1 qui s'affiche, quelle que soit la liste).
  const isCheckingEstablishments =
    isPersonActivated && establishmentsStatus === ESTABLISHMENTS_STATUS.LOADING;
  const hasEstablishmentsLoadError =
    isPersonActivated && establishmentsStatus === ESTABLISHMENTS_STATUS.ERROR;

  // Liste chargée, profil et organisme en place, mais aucun établissement
  // actif : seul l'établissement reste à faire (menu et pastille d'alerte).
  const needsEstablishment =
    isPersonActivated &&
    hasOrganization &&
    establishmentsStatus === ESTABLISHMENTS_STATUS.READY &&
    !hasValidEstablishment;

  // Tant que l'accès n'est pas complet, Organisme et Établissements restent
  // atteignables pour terminer le parcours.
  const isOnlyEstablishmentStepPending =
    isPersonActivated && hasOrganization && !isAdminUnlocked;

  // Parcours strictement séquentiel : "organisme" ne dépend que du profil
  // (sinon impossible de créer l'organisme manquant) ; "establishments" est
  // atteignable dès qu'un organisme existe, même sans établissement, sinon le
  // premier ne pourrait jamais être créé.
  const canAccessOrganisme = isPersonActivated;
  const canAccessEstablishments = isPersonActivated && hasOrganization;

  return {
    personInfo,
    isPersonActivated,
    hasOrganization,
    hasEstablishments,
    hasValidEstablishment,
    isAdminUnlocked,
    isAdminAccessBlocked,
    isOnlyEstablishmentStepPending,
    canAccessOrganisme,
    canAccessEstablishments,
    isCheckingEstablishments,
    hasEstablishmentsLoadError,
    needsEstablishment,
    refetchEstablishments,
  };
};

export default useAdminAccessControl;
